/**
 * Aggregates vocabulary entries, grammar rules, and dictionary entries
 * from the JSON files shipped under `src/data/`.
 *
 * Vite's `import.meta.glob` with `eager: true` resolves to a static map at
 * build time, so this is just a thin wrapper around the file system: any
 * JSON file dropped into `src/data/grade_{2,3,4}/` (vocabulary) or
 * `src/data/grammar_rules/` (rules) is picked up automatically.
 *
 * Dictionary entries live in a single `src/data/dictionary.json` file —
 * cross-grade and DRY: each unique word has at most one entry there, and
 * grade files never duplicate dictionary content.
 *
 * Schemas:
 *   - words/sentences:  spelling-data-pipeline/schemas/{word,sentence}_schema.json
 *   - grammar rules:    src/utils/grammarRulesValidator.js (canonical at runtime)
 *   - dictionary:       src/utils/dictionaryValidator.js   (canonical at runtime)
 */

import dictionaryRaw from './dictionary.json';
import {
  buildLookupMap,
  normalizeWordKey,
  isPopulatedEntry,
} from '../utils/dictionaryValidator.js';
import {
  isIFamilySwap,
  isDiplaSygfonaSwap,
  isOmikronOmegaSwap,
} from '../utils/scoring.js';

const vocabModules = import.meta.glob('./grade_*/*.json', { eager: true });
const ruleModules = import.meta.glob('./grammar_rules/*.json', { eager: true });

// `.flat(Infinity)` handles JSON files whose top-level array contains nested
// arrays (e.g. when entries are appended in batches as `[..., [batch2...]]`).
// Without it, those inner items end up as a single array element with no
// `.grade`, get rejected by `bucketByGrade`, and silently disappear.
const ALL_VOCAB_ENTRIES = Object.values(vocabModules)
  .flatMap((m) => m.default ?? m)
  .flat(Infinity);
const ALL_RULES = Object.values(ruleModules)
  .flatMap((m) => m.default ?? m)
  .flat(Infinity);

// Eager — small file (~few hundred KB once filled), zero benefit to lazy.
const ALL_DICTIONARY_ENTRIES = Array.isArray(dictionaryRaw) ? dictionaryRaw : [];
// O(1) lookup. Built once at module load.
const DICTIONARY_BY_KEY = buildLookupMap(ALL_DICTIONARY_ENTRIES);

export const GRADES = [2, 3, 4];

function bucketByGrade(items, predicate) {
  const out = { 2: [], 3: [], 4: [] };
  for (const item of items) {
    if (predicate && !predicate(item)) continue;
    if (out[item.grade]) out[item.grade].push(item);
  }
  return out;
}

export const wordPoolByGrade = bucketByGrade(ALL_VOCAB_ENTRIES, isWordItem);
export const sentencePoolByGrade = bucketByGrade(
  ALL_VOCAB_ENTRIES,
  isSentenceItem
);
export const rulesByGrade = bucketByGrade(ALL_RULES);

/**
 * Returns ALL vocabulary items for a grade.
 */
export function getAllItemsForGrade(grade) {
  const key = typeof grade === 'string' ? Number(grade) : grade;
  return [
    ...(wordPoolByGrade[key] || []),
    ...(sentencePoolByGrade[key] || []),
  ];
}

/**
 * Returns the pool a child actually plays from at a given grade.
 *
 *   - WORDS are cumulative: a Γ΄ kid sees Β΄+Γ΄ words; a Δ΄ kid sees all three.
 *     Past-grade words act as vocabulary revision/expansion — they're isolated
 *     entries whose `hint` stays neutral enough to reuse at any age.
 *   - SENTENCES stay grade-locked. Sentence text + `explanation` is written
 *     for a specific reading level, and mixing in younger-grade prose would
 *     feel childish for older kids.
 *
 * Used by every session/CTA/quest call-site that represents "what this kid
 * could encounter in play". `getAllItemsForGrade` (exact-grade-only) remains
 * for validators and coverage tooling that need a strict slice.
 */
export function getSessionPoolForGrade(grade) {
  const key = typeof grade === 'string' ? Number(grade) : grade;
  const words = [];
  for (const g of GRADES) {
    if (g <= key) words.push(...(wordPoolByGrade[g] || []));
  }
  return [...words, ...(sentencePoolByGrade[key] || [])];
}

/**
 * Returns the grammar rules available for a grade.
 */
export function getRulesForGrade(grade) {
  const key = typeof grade === 'string' ? Number(grade) : grade;
  return rulesByGrade[key] || [];
}

/**
 * Cumulative rules: all rules from grades ≤ `grade`, grouped by their
 * source grade. A Γ΄ kid sees Β΄ + Γ΄ rules, a Δ΄ kid sees all three,
 * mirroring the cumulative word pool of `getSessionPoolForGrade`.
 *
 * Returns an array of `{ grade, rules }` ordered from lowest grade to
 * `grade`, so the UI can render section headers in pedagogical order
 * (foundational → current).
 */
export function getCumulativeRulesForGrade(grade) {
  const key = typeof grade === 'string' ? Number(grade) : grade;
  const out = [];
  for (const g of GRADES) {
    if (g > key) continue;
    const rules = rulesByGrade[g] || [];
    if (rules.length > 0) out.push({ grade: g, rules });
  }
  return out;
}

/**
 * Lookup a single rule by id, regardless of grade.
 */
export function getRuleById(ruleId) {
  return ALL_RULES.find((r) => r.id === ruleId) ?? null;
}

/**
 * Pick the most relevant rule for a given set of error_tags + grade.
 * Used by session feedback to surface a tip tailored to the mistake.
 *
 * Preference order — the *order* of `tags` matters: the first tag in the
 * input array gets first dibs on a match.
 *   1. For each tag (in order), the first same-grade rule listing that tag.
 *   2. For each tag (in order), the first any-grade rule listing that tag.
 *   3. null
 */
export function findRuleForTags(tags, grade) {
  if (!Array.isArray(tags) || tags.length === 0) return null;
  const key = typeof grade === 'string' ? Number(grade) : grade;
  const sameGradeRules = rulesByGrade[key] || [];

  for (const tag of tags) {
    const hit = sameGradeRules.find((r) =>
      (r.related_error_tags || []).includes(tag)
    );
    if (hit) return hit;
  }
  for (const tag of tags) {
    const hit = ALL_RULES.find((r) =>
      (r.related_error_tags || []).includes(tag)
    );
    if (hit) return hit;
  }
  return null;
}

/**
 * Return the rule_refs entry that should be treated as primary.
 *
 * Priority:
 *   1. The first ref with role === 'primary'.
 *   2. The first ref in the array (regardless of role).
 *   3. null when there are no refs.
 *
 * The validator guarantees at most one explicit primary, so iteration order
 * is stable.
 */
export function getPrimaryRuleRef(refs) {
  if (!Array.isArray(refs) || refs.length === 0) return null;
  const explicit = refs.find((r) => r && r.role === 'primary');
  if (explicit) return explicit;
  return refs[0] ?? null;
}

/**
 * Pick the rule to surface in session feedback given:
 *   - the answered `item` (carries `rule_refs` + canonical `error_tags`)
 *   - `errorTags` produced by the exercise (`buildErrorTagsForAnswer`), whose
 *     first entry is the tag INFERRED from the user's actual mistake when one
 *     could be determined.
 *   - `grade` for the same-grade-first preference.
 *   - `diffChars` (optional) — `{ wrong, correct }` graphemes that differ
 *     between the child's answer and the correct one. Used to promote a
 *     dynamic /ι/-family rule when the otherwise-picked narrow rule has
 *     hardcoded characters in its template text (e.g. g3_rule_upsilon_oi
 *     talks about «οι» even when the child wrote «η»).
 *
 * Why this exists: a word may carry rule_refs pointing at the canonical
 * lesson it teaches (e.g. τονισμός), but if the child wrote a completely
 * different kind of error (e.g. ε↔α), the feedback should be about THE
 * MISTAKE the child made — not the lesson the item happens to teach.
 *
 * Resolution order:
 *   1. If the user's top inferred tag aligns with any of the item's
 *      rule_refs (primary or secondary), promote that ref to primary.
 *      When the picked rule has hardcoded characters in its template text
 *      AND the kid's diff is in the /ι/ family, upgrade to the grade's
 *      dynamic /ι/-family rule so the feedback mentions the actual
 *      grapheme the kid wrote.
 *   2. Otherwise, search the whole grade pool for a rule matching the
 *      inferred tag order (`findRuleForTags`).
 *   3. Final fallback: the item's canonical primary rule_ref, or null.
 *
 * Returns `{ primary, secondary }` where `secondary` always excludes
 * whatever ended up as `primary`.
 */
export function resolveRuleForFeedback({
  item,
  errorTags = [],
  grade,
  diffChars = null,
} = {}) {
  const { primary: refPrimary, secondary: refSecondary } = resolveRuleRefs(
    item?.rule_refs
  );
  const allRefs = [refPrimary, ...refSecondary].filter(Boolean);
  const topTag = errorTags[0] ?? null;

  // 1. Prefer the rule_ref that addresses the user's actual mistake.
  //    When multiple refs list the topTag, score them by phonetic-family
  //    coherence: a rule whose OTHER tags also belong to the same family
  //    as the topTag is more "on-topic" than one whose other tags drift
  //    into a different family. Example for επιδερμίδα with topTag=ei_i_oi:
  //      g3_rule_feminine_endings  tags=[ei_i_oi, afonos_h]   → score 1
  //      g3_rule_i_spellings       tags=[ei_i_oi, upsilon_oi] → score 2
  //    The second wins because upsilon_oi shares the /i/ family.
  //
  //    Family-coherent fallback: if no ref lists the EXACT topTag, accept
  //    refs whose own tags belong to the same phonetic family. Example
  //    for χρυσό→χρισό: inferMistakeType labels υ↔ι as 'ei_i_oi' (per
  //    CHAR_PAIR_TAGS) but the item's primary ref is g3_rule_upsilon_oi
  //    (tag 'upsilon_oi'). Both share the /ι/ family — the item's narrow
  //    rule is the right entry point, and the dynamic upgrade then
  //    substitutes the actual chars. Without this fallback, the resolver
  //    falls into step 2 and `findRuleForTags` returns the first grade
  //    rule listing 'ei_i_oi' in source order — currently the verb-ending
  //    rule g3_rule_third_person_ei, which has nothing to do with χρυσό.
  if (topTag) {
    let aligned = allRefs.filter((r) =>
      (r.related_error_tags || []).includes(topTag)
    );
    if (aligned.length === 0) {
      const topFamily = TAG_FAMILY[topTag];
      if (topFamily) {
        aligned = allRefs.filter((r) =>
          (r.related_error_tags || []).some(
            (t) => TAG_FAMILY[t] === topFamily
          )
        );
      }
    }
    if (aligned.length > 0) {
      const best = pickByFamilyCoherence(aligned, topTag);
      // Re-grade BEFORE the dynamic check so it sees the session-grade
      // rule's actual template text, not the lower-grade one's.
      const regraded = reGradeRule(best, grade);
      const upgraded = maybeUpgradeToDynamicRule(regraded, diffChars, grade);
      if (upgraded && upgraded.id !== regraded.id) {
        return {
          primary: upgraded,
          secondary: [regraded, ...allRefs.filter((r) => r.id !== best.id && r.id !== regraded.id)],
        };
      }
      return {
        primary: regraded,
        secondary: allRefs.filter((r) => r.id !== best.id && r.id !== regraded.id),
      };
    }
  }

  // 2. Look in the wider pool by the inferred tag order. Apply the same
  //    dynamic-upgrade pass as step 1 so a rule whose text has hardcoded
  //    characters (e.g. g3_rule_o_omega talks about "ο ή ω" + morphology)
  //    is replaced by the grade's dynamic rule when the kid's diff is a
  //    clean swap (ο↔ω, /ι/-family, dipla). Without this, μπαλόνι→μπαλώνι
  //    would surface the misleading static ο/ω rule because no rule_ref on
  //    the item lists `omikron_omega` and step 1 cannot fire.
  const discovered = findRuleForTags(errorTags, grade);
  if (discovered) {
    const regraded = reGradeRule(discovered, grade);
    const upgraded = maybeUpgradeToDynamicRule(regraded, diffChars, grade);
    if (upgraded && upgraded.id !== regraded.id) {
      return {
        primary: upgraded,
        secondary: [regraded, ...allRefs.filter((r) => r.id !== regraded.id && r.id !== upgraded.id)],
      };
    }
    return {
      primary: regraded,
      secondary: allRefs.filter((r) => r.id !== regraded.id),
    };
  }

  // 3. Empty errorTags is a deliberate signal from `buildErrorTagsForAnswer`
  //    that no specific tag fits the actual mistake (e.g. θρανίο→θρονίο
  //    where the diff doesn't match any character-level tag, or a
  //    trailing-letter diff like αριθμό→αριθμός). Falling through to the
  //    canonical primary would surface the very rule we filtered against,
  //    so we surface the grade's generic spelling-attention rule. When
  //    diffChars carry a clean {wrong}/{correct} pair the rule's dynamic
  //    feedback_template renders that diff verbatim; otherwise it falls
  //    back to its static tips list (still useful — "διάβασε γράμμα-
  //    γράμμα" applies to both vowel swaps and missing-letter mistakes).
  if (errorTags.length === 0) {
    return spellingAttentionFallback(grade, [refPrimary, ...refSecondary]);
  }

  // 4. Canonical fallback for everything else.
  const regradedCanonical = reGradeRule(refPrimary, grade);
  if (regradedCanonical) {
    return {
      primary: regradedCanonical,
      secondary: [...refSecondary].filter((r) => r.id !== regradedCanonical.id),
    };
  }
  // 5. No canonical to fall back on either → generic spelling-attention.
  return spellingAttentionFallback(grade, [refPrimary, ...refSecondary]);
}

// Look up the catch-all spelling-attention rule for this session grade,
// walking downward to lower grades when the session grade itself does
// not ship its own copy. Mirrors the cumulative-rules UX: a Γ΄/Δ΄ kid
// still benefits from the Β΄-grade generic rule.
function spellingAttentionFallback(grade, candidateRefs) {
  const key = typeof grade === 'string' ? Number(grade) : grade;
  let generic = null;
  for (let g = key; g >= GRADES[0]; g--) {
    generic = getRuleById(`g${g}_rule_spelling_attention`);
    if (generic) break;
  }
  const secondary = (candidateRefs || []).filter(
    (r) => r && (!generic || r.id !== generic.id)
  );
  return { primary: generic ?? null, secondary };
}

/**
 * When a rule comes from a different grade than the active session
 * (typical when a Β΄ word is reused in a Γ΄ session via the cumulative
 * word pool), swap it for a rule of the SESSION grade that teaches the
 * same error tag. Keeps the feedback text age-appropriate even though
 * the word itself is a revision item from a smaller grade.
 *
 * If no session-grade rule covers any of the original rule's tags, the
 * original is returned unchanged — a slightly off-level explanation is
 * still better than no explanation at all.
 */
function reGradeRule(rule, sessionGrade) {
  if (!rule) return null;
  const key = typeof sessionGrade === 'string' ? Number(sessionGrade) : sessionGrade;
  if (Number(rule.grade) === key) return rule;
  const tagsToTry = rule.related_error_tags || [];
  if (tagsToTry.length === 0) return rule;
  const sameGradeRules = rulesByGrade[key] || [];
  for (const tag of tagsToTry) {
    const hit = sameGradeRules.find((r) =>
      (r.related_error_tags || []).includes(tag)
    );
    if (hit) return hit;
  }
  return rule;
}

// Phonetic family that each error tag belongs to. Used by
// pickByFamilyCoherence to break ties when several rule_refs all list the
// same topTag — we prefer the rule whose OTHER tags also stay inside that
// family, since those are more clearly "the rule for that sound".
const TAG_FAMILY = {
  omikron_omega: 'o',
  epsilon_aita: 'e',
  ei_i_oi: 'i',
  upsilon_oi: 'i',
  // ou_oi_diphthong gets its own family ('u'): the /u/ sound is distinct
  // from /i/, so a rule about ου MUST NOT be considered coherent with the
  // /i/-family rules when scoring a mistake about the ου diphthong.
  ou_oi_diphthong: 'u',
  // Tags below are non-vowel: each gets its own bucket so they never count
  // as coherent with a vowel-family topTag.
  tonismos: 'accent',
  dipla_sygfona: 'doubled',
  afonos_h: 'silent',
  gender_agreement: 'gender',
  verb_ending: 'verb',
  compound_words: 'compound',
};

// Templates whose text has hardcoded grapheme references like "υ ή με οι"
// can't honestly describe a kid's mistake that involved a DIFFERENT
// grapheme (η, ει, ι, …). The dynamic /ι/-family rule sidesteps this by
// using {wrong}/{correct} placeholders that get substituted with what
// the kid actually wrote.
function ruleUsesDynamicPlaceholders(rule) {
  const tpl = rule?.feedback_template;
  if (!tpl || typeof tpl !== 'object') return false;
  for (const key of ['short', 'learning']) {
    const v = tpl[key];
    if (typeof v === 'string' && (v.includes('{wrong}') || v.includes('{correct}'))) {
      return true;
    }
  }
  return false;
}

// Returns the dynamic /ι/-family rule for the grade when (a) the kid's
// diff is a /ι/-family swap, (b) the otherwise-picked rule has hardcoded
// characters in its feedback template, and (c) the dynamic rule actually
// exists in the loaded pool. Otherwise returns null and the caller keeps
// the original pick.
function maybeUpgradeToDynamicIFamilyRule(picked, diffChars, grade) {
  if (!picked || !isIFamilySwap(diffChars)) return null;
  if (ruleUsesDynamicPlaceholders(picked)) return null;
  const key = typeof grade === 'string' ? Number(grade) : grade;
  const dynamicId = `g${key}_rule_i_family_dynamic`;
  const dynamic = getRuleById(dynamicId);
  return dynamic ?? null;
}

// Same logic as maybeUpgradeToDynamicIFamilyRule but for doubled-consonant
// swaps (θάλασσα → θάλλασσα, γράμμα → γράμα). The static dipla rules say
// "this word has a doubled consonant" but never WHICH letter the kid got
// wrong — the dynamic rule's {wrong}/{correct} placeholders fix that.
function maybeUpgradeToDynamicDiplaRule(picked, diffChars, grade) {
  if (!picked || !isDiplaSygfonaSwap(diffChars)) return null;
  if (ruleUsesDynamicPlaceholders(picked)) return null;
  const key = typeof grade === 'string' ? Number(grade) : grade;
  const dynamicId = `g${key}_rule_dipla_sygfona_dynamic`;
  const dynamic = getRuleById(dynamicId);
  return dynamic ?? null;
}

// Same logic as maybeUpgradeToDynamicIFamilyRule but for ο↔ω swaps. The
// static `g{N}_rule_o_omega` rule explains a MORPHOLOGICAL convention
// (verbs end in -ω, neuter nouns/adjectives in -ο) — accurate for ending
// mistakes but misleading when the ο/ω mistake sits in the middle of a
// noun (μπαλόνι → μπαλώνι). The dynamic rule's {wrong}/{correct}
// placeholders honestly describe the diff regardless of position.
function maybeUpgradeToDynamicOmikronOmegaRule(picked, diffChars, grade) {
  if (!picked || !isOmikronOmegaSwap(diffChars)) return null;
  if (ruleUsesDynamicPlaceholders(picked)) return null;
  const key = typeof grade === 'string' ? Number(grade) : grade;
  const dynamicId = `g${key}_rule_omikron_omega_dynamic`;
  const dynamic = getRuleById(dynamicId);
  return dynamic ?? null;
}

// Dispatcher: every kind of "specific-diff swap" gets its own dynamic
// rule. Add a new branch here when introducing a new dynamic family.
// Returns null when no upgrade applies; the caller keeps the original pick.
function maybeUpgradeToDynamicRule(picked, diffChars, grade) {
  return (
    maybeUpgradeToDynamicIFamilyRule(picked, diffChars, grade) ||
    maybeUpgradeToDynamicDiplaRule(picked, diffChars, grade) ||
    maybeUpgradeToDynamicOmikronOmegaRule(picked, diffChars, grade)
  );
}

function pickByFamilyCoherence(rules, topTag) {
  const targetFamily = TAG_FAMILY[topTag];
  if (!targetFamily) return rules[0];
  let bestIdx = 0;
  let bestScore = -1;
  for (let i = 0; i < rules.length; i++) {
    const tags = rules[i].related_error_tags || [];
    let score = 0;
    for (const t of tags) {
      if (TAG_FAMILY[t] === targetFamily) score += 1;
    }
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }
  return rules[bestIdx];
}

/**
 * Resolve a vocabulary item's `rule_refs` into actual rule objects.
 *
 * Returns:
 *   {
 *     primary: Rule | null,
 *     secondary: Rule[],  // dedup, in source order, primary excluded
 *   }
 *
 * Refs whose id does not match a loaded rule are silently dropped — the
 * validator is responsible for surfacing those.
 */
export function resolveRuleRefs(refs) {
  const empty = { primary: null, secondary: [] };
  if (!Array.isArray(refs) || refs.length === 0) return empty;

  const primaryRef = getPrimaryRuleRef(refs);
  const primary = primaryRef ? getRuleById(primaryRef.id) : null;

  const secondary = [];
  const seen = new Set(primary ? [primary.id] : []);
  for (const ref of refs) {
    if (!ref || typeof ref.id !== 'string') continue;
    if (seen.has(ref.id)) continue;
    const rule = getRuleById(ref.id);
    if (!rule) continue;
    seen.add(ref.id);
    secondary.push(rule);
  }
  return { primary, secondary };
}

/**
 * Type predicates
 */
export function isWordItem(item) {
  return typeof item?.word === 'string';
}

export function isSentenceItem(item) {
  return typeof item?.sentence === 'string';
}

/* ------------------------------------------------------------------ */
/* Dictionary access                                                   */
/* ------------------------------------------------------------------ */

/**
 * Resolve a word to its dictionary entry. Lookup is case- + accent-
 * insensitive (so "Γάτα", "γάτα" and "γατα" all match the same row).
 * Returns null when no entry exists OR when the entry exists but is still
 * a stub (no definition yet) — callers should treat both as "no data".
 */
export function getDictionaryEntry(word) {
  if (typeof word !== 'string') return null;
  const key = normalizeWordKey(word);
  if (!key) return null;
  const entry = DICTIONARY_BY_KEY.get(key) ?? null;
  if (!entry || !isPopulatedEntry(entry)) return null;
  return entry;
}

/**
 * Variant of getDictionaryEntry that returns even stub entries (used by
 * the coverage report and the maintenance UI).
 */
export function getDictionaryEntryRaw(word) {
  if (typeof word !== 'string') return null;
  const key = normalizeWordKey(word);
  if (!key) return null;
  return DICTIONARY_BY_KEY.get(key) ?? null;
}

/**
 * All populated dictionary entries — for the "Λεξικό μου" page, the
 * match-the-definition game, and any aggregate listing. Sorted by their
 * stored `word` (the JSON file is already alphabetically sorted by the
 * stub generator, so this just clones the populated subset).
 */
export function getAllDictionaryEntries() {
  return ALL_DICTIONARY_ENTRIES.filter(isPopulatedEntry);
}

/**
 * The full set (including stubs). Used by validators + coverage tooling.
 */
export function getRawDictionaryEntries() {
  return ALL_DICTIONARY_ENTRIES;
}

/**
 * Set of unique words referenced by grade files — useful for the coverage
 * test and for the stub generator's runtime sibling. O(1) afterwards.
 */
let _vocabWordCache = null;
export function getAllVocabWords() {
  if (_vocabWordCache) return _vocabWordCache;
  const out = new Set();
  for (const item of ALL_VOCAB_ENTRIES) {
    if (typeof item?.word === 'string' && item.word.trim()) {
      out.add(item.word.trim());
    } else if (typeof item?.answer === 'string' && item.answer.trim()) {
      out.add(item.answer.trim());
    }
  }
  _vocabWordCache = out;
  return out;
}

/**
 * Map: vocab item id → target word. For word items the target is `word`,
 * for sentence items it's `answer`. Used to join itemTracking records
 * (keyed by item id) to dictionary entries (keyed by word).
 */
let _itemIdToWordCache = null;
export function getItemIdToWordMap() {
  if (_itemIdToWordCache) return _itemIdToWordCache;
  const m = new Map();
  for (const item of ALL_VOCAB_ENTRIES) {
    if (typeof item?.id !== 'string') continue;
    if (typeof item.word === 'string' && item.word.trim()) {
      m.set(item.id, item.word.trim());
    } else if (typeof item.answer === 'string' && item.answer.trim()) {
      m.set(item.id, item.answer.trim());
    }
  }
  _itemIdToWordCache = m;
  return m;
}

/**
 * Given an exercise-like item, return the dictionary entries for the
 * words present in it that have populated content. Handles three shapes:
 *
 *   - Word item       — { word, ... }
 *   - Sentence item   — { sentence, answer, ... }
 *   - Rule exercise   — { prompt, answer, ... }       (grammar rules game)
 *
 * For sentence/prompt-bearing items we also surface ANY word in the
 * sentence/prompt that exists in the dictionary, so the child can look
 * up unknown words and not only the target answer.
 *
 * Pure: no React, no side effects. Filters out stub entries automatically
 * (via `getDictionaryEntry`).
 */
export function getDictionaryEntriesForItem(item) {
  if (!item) return [];
  const candidates = new Set();
  if (typeof item.word === 'string' && item.word.trim()) {
    candidates.add(item.word.trim());
  }
  if (typeof item.answer === 'string' && item.answer.trim()) {
    candidates.add(item.answer.trim());
  }
  // Collect words from any sentence-like text field on the item.
  for (const field of ['sentence', 'prompt']) {
    const text = item[field];
    if (typeof text !== 'string') continue;
    const cleaned = text
      .replace(/___/g, ' ')
      .replace(/[.,;:!?·«»"()\-]/g, ' ');
    for (const tok of cleaned.split(/\s+/)) {
      const t = tok.trim();
      if (t.length > 1) candidates.add(t);
    }
  }
  const seen = new Set();
  const out = [];
  for (const w of candidates) {
    const entry = getDictionaryEntry(w);
    if (!entry) continue;
    const key = normalizeWordKey(w);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(entry);
  }
  return out;
}
